import { CheckCircle, Clock, XCircle, User, Users, FileText, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'active' | 'pending' | 'rejected';
  users?: { id: string; name: string; designation?: string }[];
  icon: React.ReactNode;
}

interface WorkflowVisualizationProps {
  currentStatus: 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected';
  creator: { name: string; designation?: string } | null;
  reviewers: { id: string; name: string; designation?: string }[];
  approvers: { id: string; name: string; designation?: string }[];
}

export function WorkflowVisualization({ 
  currentStatus, 
  creator, 
  reviewers, 
  approvers 
}: WorkflowVisualizationProps) {
  
  const getStepStatus = (stepKey: string): 'completed' | 'active' | 'pending' | 'rejected' => {
    if (currentStatus === 'rejected') {
      return stepKey === 'draft' ? 'completed' : 'rejected';
    }
    
    const statusOrder = ['draft', 'under_review', 'pending_approval', 'approved'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    const stepIndex = statusOrder.indexOf(stepKey);
    
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  };

  const steps: WorkflowStep[] = [
    {
      id: 'draft',
      title: 'Policy Created',
      description: 'Policy drafted and ready for review',
      status: getStepStatus('draft'),
      users: creator ? [{ id: 'creator', name: creator.name, designation: creator.designation }] : [],
      icon: <FileText className="w-5 h-5" />
    },
    {
      id: 'under_review',
      title: 'Under Review',
      description: 'Policy is being reviewed by assigned reviewers',
      status: getStepStatus('under_review'),
      users: reviewers,
      icon: <Users className="w-5 h-5" />
    },
    {
      id: 'pending_approval',
      title: 'Pending Approval',
      description: 'Policy awaiting final approval',
      status: getStepStatus('pending_approval'),
      users: approvers,
      icon: <Clock className="w-5 h-5" />
    },
    {
      id: 'approved',
      title: 'Approved',
      description: 'Policy has been approved and is active',
      status: getStepStatus('approved'),
      users: approvers,
      icon: <CheckCircle className="w-5 h-5" />
    }
  ];

  const getStepStyles = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          container: 'bg-green-50 border-green-200 dark:bg-green-950/40 dark:border-green-800',
          icon: 'bg-green-500 text-white dark:bg-green-600',
          title: 'text-green-900 dark:text-green-300',
          description: 'text-green-700 dark:text-green-400',
          connector: 'bg-green-500 dark:bg-green-600'
        };
      case 'active':
        return {
          container: 'bg-blue-50 border-blue-200 ring-2 ring-blue-300 dark:bg-blue-950/40 dark:border-blue-700 dark:ring-blue-700',
          icon: 'bg-blue-500 text-white animate-pulse dark:bg-blue-600',
          title: 'text-blue-900 font-semibold dark:text-blue-300',
          description: 'text-blue-700 dark:text-blue-400',
          connector: 'bg-gray-300 dark:bg-gray-600'
        };
      case 'rejected':
        return {
          container: 'bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800',
          icon: 'bg-red-500 text-white dark:bg-red-600',
          title: 'text-red-900 dark:text-red-300',
          description: 'text-red-700 dark:text-red-400',
          connector: 'bg-red-500 dark:bg-red-600'
        };
      default:
        return {
          container: 'bg-gray-50 border-gray-200 dark:bg-gray-800/50 dark:border-gray-700',
          icon: 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-300',
          title: 'text-gray-700 dark:text-gray-300',
          description: 'text-gray-500 dark:text-gray-400',
          connector: 'bg-gray-300 dark:bg-gray-600'
        };
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-muted-foreground mb-4">
        Workflow Progress
      </div>
      
      <div className="relative">
        {steps.map((step, index) => {
          const styles = getStepStyles(step.status);
          const isLast = index === steps.length - 1;
          
          return (
            <div key={step.id} className="relative">
              {/* Step Container */}
              <div className={cn(
                "relative p-4 rounded-lg border transition-all duration-200",
                styles.container
              )}>
                {/* Step Icon and Content */}
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0",
                    styles.icon
                  )}>
                    {step.status === 'rejected' && step.id !== 'draft' ? (
                      <XCircle className="w-5 h-5" />
                    ) : (
                      step.icon
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className={cn("text-sm font-medium", styles.title)}>
                      {step.title}
                    </h4>
                    <p className={cn("text-xs mt-1", styles.description)}>
                      {step.description}
                    </p>
                    
                    {/* Users */}
                    {step.users && step.users.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {step.users.map((user, userIndex) => (
                          <div key={userIndex} className="flex items-center gap-2">
                            <User className="w-3 h-3 text-current opacity-60" />
                            <div className="text-xs">
                              <span className="font-medium">{user.name}</span>
                              {user.designation && (
                                <span className="text-current opacity-60 ml-1">
                                  • {user.designation}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Status Indicator */}
                  <div className="flex-shrink-0">
                    {step.status === 'completed' && (
                      <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />
                    )}
                    {step.status === 'active' && (
                      <div className="w-3 h-3 bg-blue-500 dark:bg-blue-400 rounded-full animate-pulse" />
                    )}
                    {step.status === 'rejected' && (
                      <XCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Connector Line */}
              {!isLast && (
                <div className="flex justify-center py-2">
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      "w-0.5 h-4",
                      step.status === 'completed' ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                    )} />
                    <ArrowDown className={cn(
                      "w-4 h-4",
                      step.status === 'completed' ? 'text-green-500 dark:text-green-600' : 'text-gray-400 dark:text-gray-500'
                    )} />
                    <div className={cn(
                      "w-0.5 h-4",
                      step.status === 'completed' ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                    )} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Status Summary */}
      <div className="mt-6 p-3 bg-muted rounded-lg">
        <div className="text-xs text-muted-foreground">
          Current Status: <span className="font-medium text-foreground">
            {currentStatus === 'draft' && 'Draft - Ready for Review'}
            {currentStatus === 'under_review' && 'Under Review - Awaiting Reviewer Feedback'}
            {currentStatus === 'pending_approval' && 'Pending Approval - Awaiting Final Approval'}
            {currentStatus === 'approved' && 'Approved - Policy is Active'}
            {currentStatus === 'rejected' && 'Rejected - Policy Needs Revision'}
          </span>
        </div>
      </div>
    </div>
  );
}
